import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Gift, Copy, Share2, Trophy, Users, DollarSign, CheckCircle2,
  Clock, Sparkles, Twitter, MessageCircle,
} from 'lucide-react';

export default function ReferralsPage() {
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        fetch(`${API}/referrals/me`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/referrals/leaderboard`).then(r => r.ok ? r.json() : { leaders: [] }),
      ]);
      setMe(m);
      setLeaders(l.leaders || []);
    } catch { toast.error('Could not load referrals'); }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    load();
  }, [authLoading, isAuthenticated, load, navigate]);

  const shareUrl = me ? `${window.location.origin}/signup?ref=${me.code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTo = (platform) => {
    if (!me) return;
    const text = `I'm building on DMV Soundstage — join with my code and we both earn when you cross $10 in creator earnings. Get in:`;
    const url = shareUrl;
    let href;
    if (platform === 'twitter') {
      href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    } else if (platform === 'sms') {
      href = `sms:?body=${encodeURIComponent(text + ' ' + url)}`;
    } else if (platform === 'native' && navigator.share) {
      navigator.share({ title: 'DMV Soundstage', text, url }).catch(() => {});
      return;
    }
    if (href) window.open(href, '_blank');
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-4">
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="referrals-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-500/15 via-fuchsia-500/10 to-violet-500/10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-500 flex items-center justify-center shadow-xl">
              <Gift className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Refer & Earn</h1>
              <p className="text-sm text-muted-foreground">
                Invite creators. When they earn <span className="text-emerald-400 font-bold">${me.milestone_usd}</span>, you both get <span className="text-emerald-400 font-bold">${me.reward_usd}</span>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1">
              <Users className="w-3 h-3" /> Signups
            </div>
            <p className="text-2xl font-black" data-testid="stat-signups">{me.signup_count}</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1">
              <CheckCircle2 className="w-3 h-3" /> Rewarded
            </div>
            <p className="text-2xl font-black text-emerald-400" data-testid="stat-rewarded">{me.rewarded_count}</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1">
              <DollarSign className="w-3 h-3" /> Earned
            </div>
            <p className="text-2xl font-black text-emerald-400" data-testid="stat-earned">${me.total_reward_usd.toFixed(2)}</p>
          </Card>
        </div>

        {/* Share card */}
        <Card className="p-6" data-testid="share-card">
          <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-2">Your invite link</p>
          <div className="flex gap-2 mb-4">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 bg-muted/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
              onClick={(e) => e.target.select()}
              data-testid="referral-url-input"
            />
            <Button onClick={copyLink} className="bg-gradient-to-r from-rose-500 to-fuchsia-500 hover:from-rose-600 hover:to-fuchsia-600" data-testid="copy-referral-link">
              {copied ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
            </Button>
          </div>
          <div className="text-center py-4 border-t border-white/10 mb-4">
            <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1">Your code</p>
            <p className="text-3xl font-black font-mono tracking-widest text-fuchsia-400" data-testid="referral-code">{me.code}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" onClick={() => shareTo('twitter')} data-testid="share-twitter">
              <Twitter className="w-4 h-4 mr-1" /> Tweet
            </Button>
            <Button variant="outline" onClick={() => shareTo('sms')} data-testid="share-sms">
              <MessageCircle className="w-4 h-4 mr-1" /> Text
            </Button>
            <Button variant="outline" onClick={() => shareTo('native')} data-testid="share-native">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
          </div>
        </Card>

        {/* How it works */}
        <Card className="p-6">
          <h3 className="font-black uppercase tracking-tight mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-400" /> How it works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: 1, t: 'Share your link', d: 'Every DM, tweet, or story = a chance.' },
              { n: 2, t: 'They join & earn $10', d: 'Any qualified earnings (streams, tips, gifts).' },
              { n: 3, t: 'You both get $5', d: 'Credited to Available balance instantly.' },
            ].map(s => (
              <div key={s.n} className="p-4 rounded-xl bg-card/40 border border-white/10">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-500 flex items-center justify-center text-white font-black mb-2">{s.n}</div>
                <p className="font-semibold text-sm">{s.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent referrals */}
        {me.recent?.length > 0 && (
          <Card className="p-6" data-testid="recent-referrals">
            <h3 className="font-black uppercase tracking-tight mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-fuchsia-400" /> Your Referrals
            </h3>
            <div className="space-y-2">
              {me.recent.map(r => (
                <div key={r.referral_id} className="flex items-center justify-between p-3 rounded-lg bg-card/40 border border-white/5" data-testid={`recent-${r.referral_id}`}>
                  <div>
                    <p className="text-sm font-semibold">{r.referred_user_id.slice(0, 12)}…</p>
                    <p className="text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    r.reward_status === 'credited'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                      : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  }`}>
                    {r.reward_status === 'credited' ? 'Rewarded' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Leaderboard */}
        <Card className="p-6" data-testid="leaderboard">
          <h3 className="font-black uppercase tracking-tight mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" /> Top Referrers
          </h3>
          {leaders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Be the first to hit the leaderboard.</p>
          ) : (
            <div className="space-y-2">
              {leaders.map((l, i) => (
                <div key={l.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-card/40 border border-white/5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${
                    i === 0 ? 'bg-amber-500/20 text-amber-400' :
                    i === 1 ? 'bg-slate-400/20 text-slate-300' :
                    i === 2 ? 'bg-orange-500/20 text-orange-400' :
                    'bg-muted/40 text-muted-foreground'
                  }`}>
                    {i + 1}
                  </div>
                  {l.avatar ? (
                    <img src={l.avatar} alt={l.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-black text-xs">
                      {(l.name || '?')[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{l.name}</p>
                    <p className="text-[11px] text-muted-foreground">{l.successful_referrals} referrals</p>
                  </div>
                  <p className="text-sm font-black text-emerald-400">${l.total_reward_usd.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
